/**
 * DD Chat Widget Coordination
 * Detects when chat widget opens/closes and coordinates UI (hides sticky ATC, drawers, etc.)
 * Click handling for [data-dd-open-chat] is in theme.liquid's inline script.
 */
(function initChatCoordination() {
  'use strict';

  if (window._ddChatCoordinationInit) return;
  window._ddChatCoordinationInit = true;

  var CHAT_OPEN_CLASS = 'dd-chat-active';
  var WIDGET_ID = 'ddg-chat-widget';
  var DRAWER_SELECTOR = '.ddg-drawer-content';
  var PANEL_SELECTOR = '.ddg-panel';
  var WATCHED_ATTRIBUTES = ['data-state', 'class', 'style', 'hidden', 'aria-hidden'];

  var _chatOpen = false;
  var _widgetEl = null;
  var _widgetObserver = null;
  var _documentObserver = null;
  var _watchdogTimer = null;
  var _widgetStateKnown = false;
  var _widgetReportedOpen = false;

  function requestClose() {
    if (!resolveChatOpenState()) {
      setChatActive(false);
      return;
    }
    window.dispatchEvent(new CustomEvent('ddg-toggle-panel'));
  }

  function handleOutsideClickClose(e) {
    if (!_chatOpen) {
      if (!resolveChatOpenState()) return;
      setChatActive(true);
    }
    // If state got stale (class says open but panel is actually closed),
    // self-heal immediately and don't swallow page clicks.
    if (!resolveChatOpenState()) {
      setChatActive(false);
      return;
    }
    var widget = document.getElementById(WIDGET_ID);
    if (!widget) return;
    if (widget.contains(e.target)) return;

    // Scrim behavior: click outside chat closes it and does not leak
    // the click to underlying page UI.
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
    requestClose();
  }

  function handleEscapeClose(e) {
    if (!e || e.key !== 'Escape') return;
    if (!_chatOpen) {
      if (!resolveChatOpenState()) return;
      setChatActive(true);
    }
    requestClose();
  }

  function setChatActive(isActive) {
    var root = document.documentElement;
    var classIsActive = root.classList.contains(CHAT_OPEN_CLASS);
    if (_chatOpen === isActive && classIsActive === isActive) return;
    _chatOpen = isActive;

    root.classList.toggle(CHAT_OPEN_CLASS, isActive);
    if (isActive) {
      startWatchdog();
    } else {
      stopWatchdog();
    }
    document.dispatchEvent(
      new CustomEvent('dd:chat:toggle', {
        detail: { open: isActive },
      })
    );
  }

  function setChatActiveFromWidgetEvent(isActive) {
    _widgetStateKnown = true;
    _widgetReportedOpen = isActive;
    setChatActive(isActive);
  }

  function startWatchdog() {
    if (_watchdogTimer) return;
    // Defensive resync: some chat close paths don't emit a reliable event,
    // and visual-only close animations can leave stale "open" assumptions.
    _watchdogTimer = window.setInterval(syncStateFromDom, 700);
  }

  function stopWatchdog() {
    if (!_watchdogTimer) return;
    window.clearInterval(_watchdogTimer);
    _watchdogTimer = null;
  }

  function queryWidget(widget, selector) {
    if (!widget) return null;
    return (widget.shadowRoot && widget.shadowRoot.querySelector(selector)) || widget.querySelector(selector);
  }

  function isDrawerOpen(widget) {
    var drawer = queryWidget(widget, DRAWER_SELECTOR);
    if (!drawer) return null;

    var state = (drawer.getAttribute('data-state') || '').toLowerCase();
    if (!state) return null;
    return state === 'open';
  }

  function isPanelVisible(widget) {
    var panel = queryWidget(widget, PANEL_SELECTOR);
    if (!panel) return false;
    if (panel.hidden) return false;
    if ((panel.getAttribute('aria-hidden') || '').toLowerCase() === 'true') return false;

    var panelState = (panel.getAttribute('data-state') || '').toLowerCase();
    if (panelState === 'closed' || panelState === 'closing') return false;

    var style = window.getComputedStyle(panel);
    if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') {
      return false;
    }

    var rect = panel.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return false;
    // Closed drawers can keep a non-zero box while translated off-screen.
    // Only treat as visible when it intersects the viewport.
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (rect.right <= 0 || rect.left >= vw || rect.bottom <= 0 || rect.top >= vh) return false;

    // If the panel is mounted but effectively inert (fully transparent),
    // only count it as open when focus is actually inside chat.
    if (style.opacity === '0') {
      return panel.contains(document.activeElement);
    }

    return true;
  }

  function resolveChatOpenState() {
    if (_widgetStateKnown) return _widgetReportedOpen;

    var widget = document.getElementById(WIDGET_ID);
    if (!widget) return false;

    var drawerOpen = isDrawerOpen(widget);
    if (drawerOpen !== null) return drawerOpen;

    return isPanelVisible(widget);
  }

  function syncStateFromDom() {
    setChatActive(resolveChatOpenState());
  }

  function scheduleDomSync() {
    requestAnimationFrame(function () {
      requestAnimationFrame(syncStateFromDom);
    });
  }

  function disconnectWidgetObserver() {
    if (_widgetObserver) {
      _widgetObserver.disconnect();
      _widgetObserver = null;
    }
  }

  function attachWidgetObserver(widget) {
    if (!widget) {
      disconnectWidgetObserver();
      _widgetEl = null;
      _widgetStateKnown = false;
      _widgetReportedOpen = false;
      setChatActive(false);
      return;
    }

    if (_widgetEl === widget && _widgetObserver) {
      syncStateFromDom();
      return;
    }

    disconnectWidgetObserver();
    _widgetEl = widget;

    _widgetObserver = new MutationObserver(syncStateFromDom);
    _widgetObserver.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: WATCHED_ATTRIBUTES,
    });

    syncStateFromDom();
  }

  function watchWidgetMount() {
    if (_documentObserver) return;

    _documentObserver = new MutationObserver(function () {
      var widget = document.getElementById(WIDGET_ID);
      if (widget !== _widgetEl) {
        attachWidgetObserver(widget);
      } else if (!widget && _widgetEl) {
        attachWidgetObserver(null);
      }
    });

    _documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    window.addEventListener('ddg-panel-state-change', function (event) {
      setChatActiveFromWidgetEvent(!!(event && event.detail && event.detail.isOpen));
    });

    /* Legacy event names from older widget builds. Route through the
       same cache-updating path as `ddg-panel-state-change` so
       `_widgetReportedOpen` stays in sync — otherwise the cached
       value silently overrides DOM-derived state on the next
       watchdog tick / outside-click / focus, leaving the chat-active
       class stuck on after a close (or off after an open). */
    window.addEventListener('ddg-panel-opened', function () {
      setChatActiveFromWidgetEvent(true);
    });

    window.addEventListener('ddg-panel-closed', function () {
      setChatActiveFromWidgetEvent(false);
    });

    window.addEventListener('ddg-toggle-panel', scheduleDomSync);
    window.addEventListener('focus', scheduleDomSync, { passive: true });
    window.addEventListener('pageshow', scheduleDomSync, { passive: true });
    document.addEventListener('visibilitychange', scheduleDomSync);
    document.addEventListener('click', handleOutsideClickClose, true);
    document.addEventListener('keydown', handleEscapeClose, true);

    watchWidgetMount();
    attachWidgetObserver(document.getElementById(WIDGET_ID));
    scheduleDomSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
